import * as React from 'react';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { scoreboardSettingsProvider } from '../data/providers/settings/scoreboardSettings';
import {
  IScoreboardData,
  scoreboardTestsProvider,
} from '../data/providers/testsProvider';
import {
  IScoreboardSettings,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  ITest,
} from '../data/types';
import { queryHashGenerator } from '../utils/queryHash';
import {
  DEFAULT_THEME,
  DEFAULT_BRANDING,
  DEFAULT_DISPLAY_CONFIG,
  IScoreboardSettings as IExtendedScoreboardSettings,
  IScoreboardTheme,
  IScoreboardDisplayConfig,
  IScoreboardBranding,
  isCurrentSettingsFormat,
} from '../ui/sections/scoreboard/types';

export interface UseScoreboardDataResult {
  tests: ITest[];
  settings: IScoreboardSettings | undefined;
  loading: boolean;
  invalidSettings: boolean;
  currentMetric: ISelectableMetric | undefined;
  currentTestType: ISelectableTestType | undefined;
  savedTheme: IScoreboardTheme;
  savedBranding: IScoreboardBranding;
  savedDisplay: IScoreboardDisplayConfig;
}

export const useScoreboardData = (): UseScoreboardDataResult => {
  const [tests, setTests] = React.useState<ITest[]>([]);
  const [settings, setSettings] = React.useState<IScoreboardSettings>();
  const [invalidSettings, setInvalidSettings] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  const currentMetric = React.useMemo((): ISelectableMetric | undefined => {
    if (!settings?.metrics || !settings?.testType?.value) return undefined;
    return settings.metrics[settings.testType.value];
  }, [settings]);

  const currentTestType = React.useMemo((): ISelectableTestType | undefined => {
    return settings?.testType;
  }, [settings]);

  React.useEffect((): (() => void) => {
    const testsSubscriber = scoreboardSettingsProvider()
      .pipe(
        switchMap(
          (mSettings: IScoreboardSettings): Observable<IScoreboardData> => {
            setLoading(true);

            // Compute additional queryHashes for split-view per-column tags
            const additionalHashes: string[] = [];
            if (
              isCurrentSettingsFormat(mSettings) &&
              mSettings.splitView?.enabled
            ) {
              const testTypeId = mSettings.testType.value;
              const leftTags = mSettings.splitView.leftColumn?.tags;
              if (leftTags?.length > 0) {
                additionalHashes.push(
                  queryHashGenerator(
                    testTypeId,
                    leftTags.map(
                      (t: ISelectableTag): string => t.value
                    )
                  )
                );
              }
              const rightTags = mSettings.splitView.rightColumn?.tags;
              if (rightTags?.length > 0) {
                additionalHashes.push(
                  queryHashGenerator(
                    testTypeId,
                    rightTags.map(
                      (t: ISelectableTag): string => t.value
                    )
                  )
                );
              }
            }

            return scoreboardTestsProvider(
              mSettings,
              additionalHashes.length > 0 ? additionalHashes : undefined
            ).pipe(
              catchError((err: Error): Observable<IScoreboardData> => {
                // eslint-disable-next-line no-console
                console.error('Scoreboard data error:', err);
                setInvalidSettings(true);
                setLoading(false);
                return of({ data: [], settings: mSettings } as IScoreboardData);
              })
            );
          }
        )
      )
      .subscribe((scoreboardData: IScoreboardData): void => {
        setTests(scoreboardData.data);
        setSettings(scoreboardData.settings);
        setLoading(false);
        setInvalidSettings(false);
      });

    return (): void => {
      testsSubscriber.unsubscribe();
    };
  }, []);

  const extendedSettings = React.useMemo(():
    | IExtendedScoreboardSettings
    | undefined => {
    if (settings && isCurrentSettingsFormat(settings)) return settings;
    return undefined;
  }, [settings]);

  const savedTheme = React.useMemo(
    (): IScoreboardTheme => extendedSettings?.theme ?? DEFAULT_THEME,
    [extendedSettings?.theme]
  );

  const savedBranding = React.useMemo(
    (): IScoreboardBranding => extendedSettings?.branding ?? DEFAULT_BRANDING,
    [extendedSettings?.branding]
  );

  const savedDisplay = React.useMemo(
    (): IScoreboardDisplayConfig =>
      extendedSettings?.display ?? DEFAULT_DISPLAY_CONFIG,
    [extendedSettings?.display]
  );

  return {
    tests,
    settings,
    loading,
    invalidSettings,
    currentMetric,
    currentTestType,
    savedTheme,
    savedBranding,
    savedDisplay,
  };
};
